/**
 * Analytics — GA4 + Meta Pixel + Google Ads.
 * Fully env-driven and no-op until IDs are provided (no fake IDs).
 * Set in frontend/.env (baked at build time):
 *   REACT_APP_GA4_ID=G-XXXXXXX
 *   REACT_APP_META_PIXEL_ID=1234567890
 *   REACT_APP_GOOGLE_ADS_ID=AW-XXXXXXX
 *   REACT_APP_GOOGLE_ADS_CHECKOUT_LABEL=xxxxx   (optional conversion label)
 *   REACT_APP_GOOGLE_TAG_ID=GT-XXXXXXX          (optional unified Google tag)
 */
const GA4_ID = process.env.REACT_APP_GA4_ID || "";
const PIXEL_ID = process.env.REACT_APP_META_PIXEL_ID || "";
const ADS_ID = process.env.REACT_APP_GOOGLE_ADS_ID || "";
const ADS_CHECKOUT_LABEL = process.env.REACT_APP_GOOGLE_ADS_CHECKOUT_LABEL || "";
const GT_ID = process.env.REACT_APP_GOOGLE_TAG_ID || "";

let started = false;

const loadScript = (src) => {
  const s = document.createElement("script");
  s.async = true;
  s.src = src;
  document.head.appendChild(s);
};

export const initAnalytics = () => {
  if (started || typeof window === "undefined") return;
  started = true;

  // Google (GA4 + Ads + unified Google tag share gtag.js)
  const gtagPrimary = GA4_ID || ADS_ID || GT_ID;
  if (gtagPrimary) {
    window.dataLayer = window.dataLayer || [];
    window.gtag = function () { window.dataLayer.push(arguments); };
    window.gtag("js", new Date());
    if (GA4_ID) window.gtag("config", GA4_ID, { send_page_view: false });
    if (ADS_ID) window.gtag("config", ADS_ID);
    // send_page_view off: trackPageView already reports GA4 page views, avoids double-count
    if (GT_ID) window.gtag("config", GT_ID, { send_page_view: false });
    loadScript(`https://www.googletagmanager.com/gtag/js?id=${gtagPrimary}`);
  }

  // Meta Pixel
  if (PIXEL_ID) {
    !(function (f, b, e, v, n, t, s) {
      if (f.fbq) return; n = f.fbq = function () { n.callMethod ? n.callMethod.apply(n, arguments) : n.queue.push(arguments); };
      if (!f._fbq) f._fbq = n; n.push = n; n.loaded = !0; n.version = "2.0"; n.queue = [];
      t = b.createElement(e); t.async = !0; t.src = v; s = b.getElementsByTagName(e)[0]; s.parentNode.insertBefore(t, s);
    })(window, document, "script", "https://connect.facebook.net/en_US/fbevents.js");
    window.fbq("init", PIXEL_ID);
  }
};

const gtagEvent = (name, params) => { if (window.gtag) window.gtag("event", name, params); };
const fbqTrack = (name, params) => { if (window.fbq) window.fbq("track", name, params); };

export const trackPageView = (path) => {
  if (GA4_ID && window.gtag) window.gtag("event", "page_view", { page_path: path, page_location: window.location.href });
  fbqTrack("PageView");
};

const gaItem = (p, qty = 1) => ({ item_id: p.slug, item_name: p.title, price: Number(p.price || 0), quantity: qty });

export const trackViewItem = (p) => {
  if (!p) return;
  gtagEvent("view_item", { currency: "USD", value: Number(p.price || 0), items: [gaItem(p)] });
  fbqTrack("ViewContent", { content_ids: [p.slug], content_name: p.title, content_type: "product", value: Number(p.price || 0), currency: "USD" });
};

export const trackAddToCart = (p, qty = 1) => {
  if (!p) return;
  const value = Number(p.price || 0) * qty;
  gtagEvent("add_to_cart", { currency: "USD", value, items: [gaItem(p, qty)] });
  fbqTrack("AddToCart", { content_ids: [p.slug], content_name: p.title, content_type: "product", value, currency: "USD" });
};

export const trackBeginCheckout = (items, value) => {
  gtagEvent("begin_checkout", {
    currency: "USD", value: Number(value || 0),
    items: (items || []).map((i) => ({ item_id: i.slug, item_name: i.title, price: Number(i.price || 0), quantity: i.qty })),
  });
  fbqTrack("InitiateCheckout", { content_ids: (items || []).map((i) => i.slug), value: Number(value || 0), currency: "USD", num_items: (items || []).reduce((s, i) => s + i.qty, 0) });
  if (ADS_ID && ADS_CHECKOUT_LABEL && window.gtag) {
    window.gtag("event", "conversion", { send_to: `${ADS_ID}/${ADS_CHECKOUT_LABEL}`, value: Number(value || 0), currency: "USD" });
  }
};

// method: "whatsapp" | "phone" | "directions"
export const trackContact = (method) => {
  gtagEvent("contact", { method });
  fbqTrack("Contact", { method });
};

export const trackLead = (source = "newsletter") => {
  gtagEvent("generate_lead", { source });
  fbqTrack("Lead", { source });
};
